/** @jsx jsx */

import type { ComponentType, HTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';
import { Box } from '@balance-web/box';
import { jsx } from '@balance-web/core';
import type { IconProps } from '@balance-web/icon';
import { Flex } from '@balance-web/flex';
import { Text } from '@balance-web/text';
import { useTheme } from '@balance-web/theme';
import { useId } from '@balance-web/utils';

// 1. Avoid focused items intersecting with the menu's border-radius
// 2. Provide consistient spacing between interior elements
const VERTICAL_RHYTHM = 'medium';

// Menu Provider
// ------------------------------

type MenuProps = HTMLAttributes<HTMLDivElement>;

export const Menu = forwardRef<HTMLDivElement, MenuProps>((props, ref) => {
  const theme = useTheme();

  return (
    <div
      ref={ref}
      css={{
        maxHeight: 320,
        minWidth: 220,
        maxWidth: 360,
        overflowY: 'auto',
        paddingBottom: theme.spacing.small,
        paddingTop: theme.spacing.small,
        WebkitOverflowScrolling: 'touch',
      }}
      role="menu"
      {...props}
    />
  );
});

// Menu Groups & Divider
// ------------------------------

type GroupProps = {
  children: ReactNode;
  title?: string;
};
export const MenuGroup = ({ children, title, ...props }: GroupProps) => {
  const { spacing } = useTheme();
  const titleId = useId();

  return (
    <div
      aria-describedby={titleId}
      css={{
        '&:not(:first-of-type)': {
          marginTop: spacing[VERTICAL_RHYTHM],
        },
      }}
      role="group"
      {...props}
    >
      {title ? (
        <Box paddingBottom="xsmall" paddingX="large">
          <Text
            as="h6"
            color="textSecondary"
            id={titleId}
            size="xsmall"
            transform="uppercase"
            weight="medium"
          >
            {title}
          </Text>
        </Box>
      ) : null}
      {children}
    </div>
  );
};

export const MenuDivider = () => {
  const { semantic, spacing } = useTheme();

  return (
    <div
      css={{
        backgroundColor: semantic.colour.surface.neutral.border,
        height: 1,
        marginBottom: spacing[VERTICAL_RHYTHM],
        marginTop: spacing[VERTICAL_RHYTHM],
      }}
      role="separator"
    />
  );
};

// Menu Item
// ------------------------------

type MenuItemProps = {
  /** Determines if the menu item trigger is disabled */
  disabled?: boolean;
  /** An optional icon placed to the left of the label */
  icon?: ComponentType<IconProps>;
  /** Describe the action of the item with the label */
  label: string;
  /** An event handler for when the item is clicked */
  onClick?: (event: MouseEvent) => void;
  /** The tone of the action this item represents */
  tone?: 'active' | 'passive' | 'critical';
} & HTMLAttributes<HTMLButtonElement>;

export const MenuItem = forwardRef<HTMLButtonElement, MenuItemProps>(
  (props, ref) => {
    const { label, icon: Icon, tone = 'passive', ...consumerProps } = props;
    const { semantic, sizing } = useTheme();
    const height = sizing.base; // FIXME: should be resolved by the `Box` component
    const highlightedStyles = {
      background: semantic.colour.neutral.container.interactions.hover,
      outline: 'none !important',
    };

    const toneMap = {
      active: 'textLink',
      passive: 'textPrimary',
      critical: 'critical',
    } as const;
    const colorKey = toneMap[tone];

    // NOTE: must be a button element so click events extend to "Enter/Space" keypress
    return (
      <Flex
        ref={ref}
        as="button"
        css={{
          ':hover,:focus': highlightedStyles,
          ':active': {
            backgroundColor:
              semantic.colour.neutral.container.interactions.pressed,
          },
          ':disabled': {
            pointerEvents: 'none',
          },
        }}
        height={height}
        paddingX="large"
        role="menuitem"
        tabIndex={0}
        width="100%"
        gap="small"
        // style
        cursor="pointer"
        type="button"
        // flex
        alignItems="center"
        {...consumerProps}
      >
        {Icon ? (
          <Icon
            color={props.disabled ? 'textDisabled' : colorKey}
            size="small"
          />
        ) : null}
        <Box flex={1} maxWidth="100%" minWidth={0}>
          <Text
            align="left"
            color={props.disabled ? 'textDisabled' : colorKey}
            leading="tight" // TODO: remove (after capsize implementation)
            overflowStrategy="truncate"
            size="small"
            weight="medium"
          >
            {label}
          </Text>
        </Box>
      </Flex>
    );
  }
);
